import { forwardRef } from 'react';
import { FieldProps } from './types';
import clsx from 'clsx';
import IconError from '../../icons/IconError';
import IconCheck from '../../icons/IconCheck';
import Tooltip from '@ui/Tooltip';
import IconQuestionMark from '@icons/IconQuestionMark';

/**
 *  UI Component
 */
const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(
  props,
  ref
) {
  const {
    className,
    children,
    appendedChildren,
    labelEndAdornment,
    label,
    startAdornment,
    endAdornment,
    size,
    focused,
    raised,
    error,
    success,
    helperText,
    required,
    tooltip,
    ...rest
  } = props;

  const cls = styleField(props);

  let statusAdornment;
  if (error) {
    statusAdornment = <IconError className={clsx(cls.statusIcon)} />;
  } else if (success) {
    statusAdornment = <IconCheck className={clsx(cls.statusIcon)} />;
  }

  return (
    <div className={clsx(cls.main, className)} ref={ref} {...rest}>
      {label && (
        <div className="flex gap-2">
          <label className={clsx(cls.label)}>
            {label}
            {required && '*'}
          </label>
          {labelEndAdornment}
          {tooltip && (
            <Tooltip tip={tooltip}>
              <IconQuestionMark className="cursor-pointer" size={10} />
            </Tooltip>
          )}
        </div>
      )}
      <div className={clsx(cls.inputGroup)}>
        {startAdornment && (
          <span className={clsx(cls.adornment)}>{startAdornment}</span>
        )}
        {children}
        {endAdornment && (
          <span className={clsx(cls.adornment)}>{endAdornment}</span>
        )}
        {statusAdornment && (
          <span className={clsx(cls.adornment)}>{statusAdornment}</span>
        )}
      </div>
      {appendedChildren}
      {helperText && (
        <div className={clsx(cls.helperText)}>
          {Array.isArray(helperText) ? (
            helperText.map((text, index) => <p key={index}>{text}</p>)
          ) : (
            <p>{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
});

export default Field;

/**
 * Assign classNames to component based on properties
 */
function styleField(props: FieldProps) {
  const mc = 'Field';
  const mcLabel = `${mc}__Label`;
  const mcGroup = `${mc}__InputGroup`;
  const mcInput = `${mc}__Input`;
  const mcAdornment = `${mc}__Adornment`;
  const mcHelperText = `${mc}__HelperText`;
  const mcStatusIcon = `${mc}__StatusIcon`;
  const cls = {
    main: [mc],
    label: [mcLabel],
    inputGroup: [mcGroup],
    input: [mcInput],
    adornment: [mcAdornment],
    helperText: [mcHelperText],
    statusIcon: [mcStatusIcon]
  };

  if (props.focused) {
    cls.main.push(`${mc}--focused`);
  }
  if (props.size) {
    cls.main.push(`${mc}--${props.size}`);
  }
  if (props.variant) {
    cls.main.push(`${mc}--${props.variant}`);
  }
  if (props.raised) {
    cls.main.push(`${mc}--raised`);
  }
  if (props.error) {
    cls.main.push(`${mc}--error`);
  }
  if (props.success) {
    cls.main.push(`${mc}--success`);
  }
  return cls;
}
